import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    canvas.width = width;
    canvas.height = height;

    const particles: {
      x: number; y: number; vx: number; vy: number;
      radius: number; alpha: number; color: string;
    }[] = [];

    const colors = ['#00e5a0', '#00c9a7', '#00b4d8', '#48cae4', '#90e0ef'];

    for (let i = 0; i < 80; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 0.5,
        alpha: Math.random() * 0.6 + 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    const drawGrid = () => {
      ctx.strokeStyle = 'rgba(0, 229, 160, 0.04)';
      ctx.lineWidth = 1;
      const gridSize = 60;
      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    };

    const drawConnections = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 229, 160, ${0.12 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      drawGrid();
      drawConnections();

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
        ctx.globalAlpha = 1;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Dark Gradient Base */}
      <div className="absolute inset-0 z-0" style={{
        background: 'linear-gradient(135deg, #020c14 0%, #041a2e 30%, #061f2a 55%, #030f1a 80%, #010a12 100%)'
      }} />

      {/* Glowing Orbs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,229,160,0.08) 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-15%] right-[-5%] w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,180,216,0.07) 0%, transparent 70%)' }} />
        <div className="absolute top-[40%] left-[50%] w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(0,229,160,0.05) 0%, transparent 70%)', transform: 'translate(-50%, -50%)' }} />
      </div>

      {/* Animated Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0 w-full h-full"
      />

      {/* Subtle scanline texture */}
      <div className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,229,160,0.012) 2px, rgba(0,229,160,0.012) 4px)'
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center w-full">
        {/* Trust Badge */}
        <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full mb-7"
          style={{ boxShadow: '0 0 20px rgba(0,229,160,0.08)' }}>
          <i className="ri-verified-badge-fill text-yellow-400 text-base"></i>
          <span className="text-white text-xs font-medium">
            Trusted by 200+ CBSE/ICSE Schools | NEP 2020 Aligned
          </span>
        </div>

        {/* Main Headline */}
        <h1 className="text-4xl md:text-6xl font-bold text-white mb-5 leading-tight">
          Transform Your School Into a<br />
          <span style={{ color: '#00e5a0' }}>Future-Ready</span> STEM Innovation Hub
        </h1>

        {/* Subheadline */}
        <p className="text-base md:text-lg text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
          End-to-end STEM, AI &amp; Robotics lab setup with annual academic integration.<br />
          Complete infrastructure, trained instructors, and curriculum-aligned programs.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
          <button
            onClick={() => {
              const btn = document.querySelector<HTMLElement>('#vapi-widget-floating-button');
              if (btn) btn.click();
            }}
            className="px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer flex items-center gap-2"
            style={{ background: 'linear-gradient(135deg, #00c9a7, #00e5a0)', color: '#020c14' }}
          >
            <i className="ri-robot-2-line text-base"></i>
            Book a Free Demo
          </button>
          <a
            href="#contact"
            className="px-6 py-3 backdrop-blur-md border text-white text-sm font-semibold rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
            style={{ borderColor: 'rgba(0,229,160,0.4)', background: 'rgba(0,229,160,0.05)' }}
          >
            Schedule Principal Consultation
            <i className="ri-arrow-right-line text-base"></i>
          </a>
          <button
            onClick={() => navigate('/solutions')}
            className="px-6 py-3 bg-transparent border border-white/20 text-white text-sm font-semibold rounded-lg hover:bg-white/5 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-eye-line text-base"></i>
            View Solutions
          </button>
        </div>

        {/* Value Props */}
        <div className="flex flex-wrap items-center justify-center gap-4 max-w-4xl mx-auto">
          {[
            { label: 'NEP 2020 Aligned Curriculum' },
            { label: 'Industry-Certified Instructors' },
            { label: 'Complete Lab Infrastructure' },
            { label: '12-Month Partnership Guarantee' },
          ].map((prop, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 px-5 py-3 backdrop-blur-md border rounded-full transition-all"
              style={{ background: 'rgba(0,229,160,0.06)', borderColor: 'rgba(0,229,160,0.2)' }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-lg" style={{ color: '#00e5a0' }}></i>
              </div>
              <span className="text-white text-sm font-medium whitespace-nowrap">{prop.label}</span>
            </div>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="flex flex-col items-center gap-2">
            <span className="text-gray-400 text-xs uppercase tracking-wider">Explore Our Solutions</span>
            <i className="ri-arrow-down-line text-gray-400 text-2xl"></i>
          </div>
        </div>
      </div>
    </section>
  );
}
